import { ColumnDef, DeepKeys } from '@tanstack/react-table';
import { AccessorColumnDefinition, DisplayColumnDefinition } from './types';

export type MetaValueConfiguration = {
  mobileHeaderCount: number;
};

/**
 * This function is a helper function to create column definitions.
 * It provides type safety to the values in the renderCell function,
 * which removes the need for type checking on this level.
 */
export const isDisplayColumn = <TDefinition>(
  column: AccessorColumnDefinition<TDefinition, DeepKeys<TDefinition>> | DisplayColumnDefinition<TDefinition>,
): column is DisplayColumnDefinition<TDefinition> => column.type === 'display';

export const makeAccessorColumnBuilder =
  <TDefinition>() =>
  <TKey extends DeepKeys<TDefinition>>(
    columnId: TKey,
    definition: Omit<AccessorColumnDefinition<TDefinition, TKey>, 'columnId' | 'type'>,
  ) => {
    return {
      ...definition,
      type: 'accessor' as const,
      columnId,
    };
  };

/**
 * Helper to generate columns that are not based on a specific data field.
 */
export const createDisplayColumn = <TDefinition>(definition: Omit<DisplayColumnDefinition<TDefinition>, 'type'>) => ({
  ...definition,
  type: 'display' as const,
});

export const withMetaValues = <T>(
  // https://github.com/TanStack/table/issues/4382
  // there's an issue with the typing of ColumnDef
  columns: ColumnDef<T, any>[], // eslint-disable-line @typescript-eslint/no-explicit-any
  { mobileHeaderCount }: MetaValueConfiguration = {
    mobileHeaderCount: 0,
  },
) => {
  const hiddenColumns = columns.filter((colDef) => colDef.meta?.isHidden);
  const visibleColumns = columns.filter((colDef) => !colDef.meta?.isHidden);

  return [
    ...visibleColumns.map((def, index) => ({
      ...def,
      meta: { ...def.meta, sticky: false, isMobileHeader: index < mobileHeaderCount },
    })),
    ...hiddenColumns,
  ];
};
